import React, { useState } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

export function GardenPlotEstimatorTool() {
  const [beds, setBeds] = useState('10');
  const [length, setLength] = useState('8');
  const [width, setWidth] = useState('4');
  const [depthInches, setDepthInches] = useState('12');
  
  const [soilPricePerYard, setSoilPricePerYard] = useState('45');
  const [lumberPricePerFt, setLumberPricePerFt] = useState('3.50');

  const b = parseInt(beds) || 0;
  const l = parseFloat(length) || 0;
  const w = parseFloat(width) || 0;
  const d = parseFloat(depthInches) || 0;
  
  const sp = parseFloat(soilPricePerYard) || 0;
  const lp = parseFloat(lumberPricePerFt) || 0;

  // Soil volume calculation
  // V = L * W * (D/12) cubic feet per bed
  const cuFtPerBed = l * w * (d / 12);
  const totalCuFt = cuFtPerBed * b;
  const totalCuYards = totalCuFt / 27; // 27 cubic feet in a cubic yard
  const totalSoilCost = totalCuYards * sp;

  // Lumber calculation
  const linearFeetPerBed = (l * 2) + (w * 2);
  const totalLinearFeet = linearFeetPerBed * b;
  // Account for depth (if using e.g., 2x6 boards, 12" depth needs 2 boards high)
  const boardsHigh = Math.ceil(d / 6); // Assuming standard ~6" actual width boards
  const actualLumberNeeded = totalLinearFeet * boardsHigh;
  const totalLumberCost = actualLumberNeeded * lp;

  const totalCost = totalSoilCost + totalLumberCost;

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Plot Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Number of Beds</label>
              <Input type="number" value={beds} onChange={e => setBeds(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Depth (inches)</label>
              <Input type="number" value={depthInches} onChange={e => setDepthInches(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Length (ft)</label>
              <Input type="number" value={length} onChange={e => setLength(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Width (ft)</label>
              <Input type="number" value={width} onChange={e => setWidth(e.target.value)} />
            </div>
          </div>
        </Card>
        
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Material Pricing</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Soil ($ / Cu. Yard)</label>
              <Input type="number" value={soilPricePerYard} onChange={e => setSoilPricePerYard(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Lumber ($ / Linear Ft.)</label>
              <Input type="number" value={lumberPricePerFt} onChange={e => setLumberPricePerFt(e.target.value)} />
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-6 flex flex-col justify-center">
        <Card className="p-6 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 h-full flex flex-col justify-center">
            <span className="text-[10px] uppercase font-bold block mb-2 tracking-widest text-emerald-600">Total Material Cost Estimate</span>
            <p className="text-6xl font-black font-mono mb-6 text-emerald-700 tracking-tighter">${Math.ceil(totalCost).toLocaleString()}</p>
            
            <div className="space-y-3 font-mono text-sm border-t border-emerald-200/50 pt-4 dark:border-emerald-800/50">
               <div className="flex justify-between text-emerald-800">
                 <span className="opacity-80">Soil Needed</span>
                 <span className="font-bold">{totalCuYards.toFixed(1)} cy (${Math.round(totalSoilCost)})</span>
               </div>
               <div className="flex justify-between text-emerald-800">
                 <span className="opacity-80">Lumber Needed</span>
                 <span className="font-bold">{actualLumberNeeded} lf (${Math.round(totalLumberCost)})</span>
               </div>
               <div className="text-xs text-emerald-700/60 mt-4 leading-tight">
                 Assumes standard 2-inch dimensional lumber geometry (6-inch face equivalent) mapped to requested depth.
               </div>
            </div>
        </Card>
      </div>
    </div>
  );
}

